import {useCallback, useEffect} from 'react';
import {useSalesStore} from '../store/salesStore';
import {useSalesUIStore} from '../store/salesUIStore';
import {useNotification} from '../../../Services/useNotification';
import {Sale} from '../models/sale.model';
import {salesFactory} from '../store/salesFactory';

/**
 * Unified hook for sales management that combines data and UI state
 * This replaces the need for multiple useState calls in components
 */
export const useSalesManagement = () => {
  // Data stores
  const salesStore = useSalesStore();
  const uiStore = useSalesUIStore();
  const {success: showSuccess} = useNotification();

  // Destructure from salesStore
  const {
    sales, statistics, loading, error, pagination,
    fetchSales, fetchStatistics, fetchSaleById, fetchSaleByInvoiceNumber,
    updateSale, deleteSale, clearError, setPagination
  } = salesStore;

  // Destructure from uiStore
  const {
    activeTab, setActiveTab,
    currentPage, pageSize, setCurrentPage, setPageSize,
    searchText, setSearchText,
    selectedSale, isDetailsModalOpen, loadingDetails,
    setSelectedSale, openDetailsModal, closeDetailsModal, setLoadingDetails,
    showFilters, setShowFilters,
    reset: resetUI
  } = uiStore;


  // Initialize the store with use cases on first use
  useEffect(() => {
    salesFactory.initializeStore();
  }, []);

  // Load data when dependencies change
  useEffect(() => {
    const trimmedSearch = searchText.trim();
    const filters = trimmedSearch ? {
      search: trimmedSearch
    } : undefined;
    fetchSales(currentPage, pageSize, filters);
    fetchStatistics();
  }, [currentPage, pageSize, searchText, fetchSales, fetchStatistics]);

  // Unified Actions
  const handleTabChange = useCallback((tab: 'sales' | 'statistics') => {
    setActiveTab(tab);
  }, [setActiveTab]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, [setCurrentPage]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
  }, [setPageSize]);

  const handleSearchChange = useCallback((text: string) => {
    setSearchText(text);
  }, [setSearchText]);

  const handleViewDetails = useCallback(async (sale: Sale) => {
    setLoadingDetails(true);
    // Open the modal with basic sale data first, then update with detailed data
    openDetailsModal(sale);

    try {
      const detailedSale = await fetchSaleById(sale.id);

      if (detailedSale) {
        setSelectedSale(detailedSale);
        // Return the detailed sale data to the SalesTable component
        return detailedSale;
      } else {
        setSelectedSale(sale);
        return sale;
      }
    } catch (error) {
      setSelectedSale(sale);
      return sale;
    } finally {
      setLoadingDetails(false);
    }
  }, [fetchSaleById, setSelectedSale, setLoadingDetails, openDetailsModal]);

  const handleCloseDetailsModal = useCallback(() => {
    closeDetailsModal();
  }, [closeDetailsModal]);

  const handleUpdateSale = useCallback(async (id: string, saleData: Partial<Sale>) => {
    try {
      await updateSale(id, saleData);
      showSuccess('تم تحديث المبيعة بنجاح');
      // Refresh data
      const trimmedSearch = searchText.trim();
      const filters = trimmedSearch ? {search: trimmedSearch} : undefined;
      fetchSales(currentPage, pageSize, filters);
    } catch (error) {
    }
  }, [updateSale, searchText, currentPage, pageSize, fetchSales]);

  const handleDeleteSale = useCallback(async (id: string) => {
    try {
      await deleteSale(id);
      showSuccess('تم حذف المبيعة بنجاح');
      // Refresh data
      const trimmedSearch = searchText.trim();
      const filters = trimmedSearch ? {search: trimmedSearch} : undefined;
      fetchSales(currentPage, pageSize, filters);
    } catch (error) {
    }
  }, [deleteSale, searchText, currentPage, pageSize, fetchSales]);

  const handleRefresh = useCallback(() => {
    const trimmedSearch = searchText.trim();
    const filters = trimmedSearch ? {search: trimmedSearch} : undefined;
    fetchSales(currentPage, pageSize, filters);
    fetchStatistics();
  }, [searchText, currentPage, pageSize, fetchSales, fetchStatistics]);

  // Reset all stores
  const resetAllStores = useCallback(() => {
    clearError();
    resetUI();
  }, [clearError, resetUI]);

  return {
    // UI State
    activeTab, setActiveTab,
    currentPage, pageSize, setCurrentPage, setPageSize,
    searchText, setSearchText,
    selectedSale, isDetailsModalOpen, loadingDetails,
    showFilters, setShowFilters,

    // Data
    sales, statistics, pagination,

    // Loading States
    loading,

    // Error States
    error,

    // Actions
    handleTabChange, handlePageChange, handlePageSizeChange,
    handleSearchChange, handleViewDetails, handleCloseDetailsModal,
    handleUpdateSale, handleDeleteSale, handleRefresh,
    resetAllStores,

    // Modal Actions
    openDetailsModal, closeDetailsModal,

    // Data Actions
    fetchSales, fetchStatistics, fetchSaleById, fetchSaleByInvoiceNumber,
    updateSale, deleteSale, clearError
  };
};
